import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { ApiError } from '../../api/client';
import type { ComplimentaryCareEnrollment, Enrollee, EnrolleeSubmission } from '../../api/types';
import { ClinicField } from '../../components/ClinicField';
import { DocumentPreviewModal, type DocumentPreviewTarget } from '../../components/DocumentPreviewModal';
import { EnrollmentStatusBadge } from '../../components/EnrollmentStatusBadge';
import { describeExistingCare, relationshipLabel } from '../../components/enrollmentLabels';
import { useClinicDirectory } from '../../hooks/useClinicDirectory';
import { useHubContext } from '../../shell/HubContext';
import { SubmitConfirmModal } from './SubmitConfirmModal';
import { describeDraftChanges, type Draft, type DraftChanges, type DraftEnrollee } from './draftChanges';

// Mirrors ComplimentaryCareOptions on the API — kept in sync by hand since there is no shared schema
// endpoint for this form the way the onboarding email template has one.
const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;
const ALLOWED_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png'];
const MAX_ELIGIBLE_CHILD_AGE = 26;
const MAX_CHILDREN = 5;

type SpouseType = 'spouse' | 'domesticPartner';

interface SpouseDraft {
  /** Present when editing someone already on the enrollment. */
  id?: string;
  type: SpouseType;
  firstName: string;
  lastName: string;
  age: string;
  contactPhoneNumber: string;
  file: File | null;
  existingDocumentFileName: string | null;

  /**
   * Which relationship the document on file was attached for. A marriage certificate does not prove a
   * domestic partnership and an affidavit does not prove a marriage, so switching between the two
   * retires the document even though nothing else about the person changed.
   */
  documentRelationship: SpouseType | null;
}

interface ChildDraft extends Omit<SpouseDraft, 'type' | 'documentRelationship'> {
  key: string;
}

const emptySpouse: SpouseDraft = {
  type: 'spouse',
  firstName: '',
  lastName: '',
  age: '',
  contactPhoneNumber: '',
  file: null,
  existingDocumentFileName: null,
  documentRelationship: null,
};

function emptyChild(): ChildDraft {
  return {
    key: crypto.randomUUID(),
    firstName: '',
    lastName: '',
    age: '',
    contactPhoneNumber: '',
    file: null,
    existingDocumentFileName: null,
  };
}

/**
 * The document already on file, or null when it no longer proves the relationship being claimed —
 * which is exactly the spouse ↔ domestic partner switch. Null means "a new file is required", both
 * for validation and for what the field shows.
 */
function usableExistingDocument(spouse: SpouseDraft): string | null {
  return spouse.documentRelationship === spouse.type ? spouse.existingDocumentFileName : null;
}

/**
 * A stored "YYYY-MM-DD" for display. Split rather than passed to the Date constructor, which reads a
 * bare ISO date as UTC midnight and so shows the day before across the Americas.
 */
function formatIsoDate(iso: string): string {
  const [year, month, day] = iso.split('-').map(Number);

  return Number.isFinite(year) && Number.isFinite(month) && Number.isFinite(day)
    ? new Date(year, month - 1, day).toLocaleDateString()
    : iso;
}

/**
 * Today as the date input spells it. Built from the local calendar rather than toISOString(), which
 * is UTC — west of Greenwich that is tomorrow for part of the evening, and it would offer a "today"
 * the employee then gets told is in the future.
 */
function todayIso(): string {
  const now = new Date();
  const month = `${now.getMonth() + 1}`.padStart(2, '0');
  const day = `${now.getDate()}`.padStart(2, '0');

  return `${now.getFullYear()}-${month}-${day}`;
}

/**
 * Whether a three-digit NANP code can actually be assigned: it starts 2-9, and is not an N11 service
 * code like 411 or 911. Mirrors PhoneValidation.IsAssignableCode on the API.
 */
function isAssignableCode(code: string): boolean {
  return /^[2-9]/.test(code) && !/^\d11$/.test(code);
}

/** Mirrors PhoneValidation on the API: North American, area code included, and actually dialable. */
function validatePhone(raw: string, required: boolean): string | undefined {
  if (raw.trim().length === 0) {
    return required ? 'Enter a contact phone number.' : undefined;
  }

  const digits = raw.replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '');

  if (digits.length !== 10) {
    return 'Enter all ten digits — the area code goes in the brackets.';
  }

  // Ten digits is not the same as a number the clinic can ring: (000) 000-0000 counts to ten too.
  if (!isAssignableCode(digits.slice(0, 3)) || !isAssignableCode(digits.slice(3, 6))) {
    return 'That is not a number that can be dialled — check the area code and the three digits after it.';
  }

  return undefined;
}

function digitsOf(value: string): string {
  const digits = value.replace(/\D/g, '');

  // A pasted number often carries the country code. What follows it is the area-code-first part the
  // field is shaped around, so keep that and drop the 1.
  return (digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits).slice(0, 10);
}

/**
 * Types the number into its North American shape as it is entered — "(312) 555-0134" — so the field
 * itself says the area code comes first. It replaces the line of help text that used to say so, which
 * everyone read after filling the field in rather than before.
 */
function formatPhoneInput(next: string, previous: string): string {
  let digits = digitsOf(next);

  // Backspacing over a bracket, the space, or the dash deletes no digit, so re-formatting would put
  // the character straight back and the caret would sit there forever. Take the digit in front of it
  // instead, which is what the keystroke meant.
  if (next.length < previous.length && digits === digitsOf(previous)) {
    digits = digits.slice(0, -1);
  }

  if (digits.length === 0) {
    return '';
  }
  if (digits.length <= 3) {
    return `(${digits}`;
  }
  if (digits.length <= 6) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  }

  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function validateDocument(file: File | null, existingDocumentFileName: string | null): string | undefined {
  if (!file) {
    // On an edit the document already on file counts — only a brand new enrollee has to attach one.
    return existingDocumentFileName ? undefined : 'Attach the required document.';
  }

  const extension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();

  if (!ALLOWED_EXTENSIONS.includes(extension)) {
    return `'${extension}' files are not accepted. Allowed types: ${ALLOWED_EXTENSIONS.join(', ')}.`;
  }

  if (file.size > MAX_UPLOAD_BYTES) {
    return `The file is larger than the ${MAX_UPLOAD_BYTES / (1024 * 1024)} MB limit.`;
  }

  return undefined;
}

/**
 * The digital "Employee Complimentary Chiropractic Care Enrollment" form: the team member plus,
 * optionally, their spouse or domestic partner and up to five children.
 *
 * An employee has exactly one enrollment. Opening the form again edits that one, pre-filled with
 * what they submitted before — and any edit sends it back to HR for review.
 */
export function ComplimentaryCareEnrollmentSection() {
  const { api, userLabel } = useHubContext();

  // The enrollment itself, and the change waiting on HR if there is one. Kept apart because they are
  // different answers to different questions — see EnrollmentStanding below.
  const [enrollment, setEnrollment] = useState<ComplimentaryCareEnrollment | null>(null);
  const [proposedEdit, setProposedEdit] = useState<ComplimentaryCareEnrollment | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);

  const [hireDate, setHireDate] = useState('');
  const [alreadyReceivingCareElsewhere, setAlreadyReceivingCareElsewhere] = useState(false);
  const [alreadyReceivingCareWho, setAlreadyReceivingCareWho] = useState('');
  const [alreadyReceivingCareClinic, setAlreadyReceivingCareClinic] = useState('');
  const [teamMemberWellnessCenter, setTeamMemberWellnessCenter] = useState('');

  // Both clinic questions draw on this one list — see useClinicDirectory and ClinicField, which the
  // two public forms fill their own clinic field from as well.
  const loadClinics = useCallback((signal: AbortSignal) => api.listComplimentaryCareClinics(signal), [api]);
  const clinics = useClinicDirectory(loadClinics);

  const [teamMemberContactPhoneNumber, setTeamMemberContactPhoneNumber] = useState('');

  const [includeSpouse, setIncludeSpouse] = useState(false);
  const [spouse, setSpouse] = useState<SpouseDraft>(emptySpouse);
  const [children, setChildren] = useState<ChildDraft[]>([]);

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState<DocumentPreviewTarget | null>(null);

  // Children taken off the form during this edit, newest first, so a mis-click is one click to undo.
  // Nothing is lost until the form is actually submitted.
  const [removedChildren, setRemovedChildren] = useState<{ at: number; child: ChildDraft }[]>([]);

  const [confirming, setConfirming] = useState<DraftChanges | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    (async () => {
      try {
        const mine = await api.getMyComplimentaryCareEnrollment(controller.signal);
        setEnrollment(mine.enrollment);
        setProposedEdit(mine.proposedEdit);
        setLoaded(true);
      } catch (err) {
        if (!controller.signal.aborted) {
          setLoadError(err instanceof Error ? err.message : 'Could not load your enrollment.');
        }
      }
    })();

    return () => controller.abort();
  }, [api]);

  async function reload() {
    const mine = await api.getMyComplimentaryCareEnrollment();
    setEnrollment(mine.enrollment);
    setProposedEdit(mine.proposedEdit);
  }

  // The form opens over the change if one is outstanding, otherwise over the enrollment itself —
  // whichever the employee last saw.
  const editBase = proposedEdit ?? enrollment;

  /** Opens the form over whatever is on file, so an edit starts from the current answers. */
  function startEditing(from: ComplimentaryCareEnrollment | null) {
    setFieldErrors({});
    setSubmitError(null);
    setRemovedChildren([]);

    if (!from) {
      setHireDate('');
      setAlreadyReceivingCareElsewhere(false);
      setAlreadyReceivingCareWho('');
      setAlreadyReceivingCareClinic('');
      setTeamMemberWellnessCenter('');
      setTeamMemberContactPhoneNumber('');
      setIncludeSpouse(false);
      setSpouse(emptySpouse);
      setChildren([]);
      setEditing(true);
      return;
    }

    setHireDate(from.hireDate);
    setAlreadyReceivingCareElsewhere(from.alreadyReceivingCareElsewhere);
    setAlreadyReceivingCareWho(from.alreadyReceivingCareWho ?? '');
    setAlreadyReceivingCareClinic(from.alreadyReceivingCareClinic ?? '');

    const teamMember = from.enrollees.find((e) => e.relationship === 'teamMember');
    setTeamMemberWellnessCenter(teamMember?.wellnessCenter ?? '');
    setTeamMemberContactPhoneNumber(teamMember?.contactPhoneNumber ?? '');

    const partner = from.enrollees.find((e) => e.relationship === 'spouse' || e.relationship === 'domesticPartner');
    const partnerType: SpouseType = partner?.relationship === 'domesticPartner' ? 'domesticPartner' : 'spouse';

    setIncludeSpouse(partner !== undefined);
    setSpouse(
      partner
        ? {
            id: partner.id,
            type: partnerType,
            firstName: partner.firstName,
            lastName: partner.lastName,
            age: partner.age?.toString() ?? '',
            contactPhoneNumber: partner.contactPhoneNumber ?? '',
            file: null,
            existingDocumentFileName: partner.documentFileName,
            documentRelationship: partner.documentFileName ? partnerType : null,
          }
        : emptySpouse,
    );

    setChildren(
      from.enrollees
        .filter((e) => e.relationship === 'child')
        .map((child) => ({
          key: crypto.randomUUID(),
          id: child.id,
          firstName: child.firstName,
          lastName: child.lastName,
          age: child.age?.toString() ?? '',
          contactPhoneNumber: child.contactPhoneNumber ?? '',
          file: null,
          existingDocumentFileName: child.documentFileName,
        })),
    );

    setEditing(true);
  }

  function validate(): Record<string, string> {
    const errors: Record<string, string> = {};

    if (hireDate.trim().length === 0) {
      errors.hireDate = 'Enter your hire date.';
    } else if (hireDate > todayIso()) {
      // The date picker's own max stops the calendar, but not a typed or pasted date.
      errors.hireDate = 'Your hire date cannot be in the future.';
    }

    if (alreadyReceivingCareElsewhere && alreadyReceivingCareWho.trim().length === 0) {
      errors.alreadyReceivingCareWho = 'Say who is already being seen.';
    }

    if (alreadyReceivingCareElsewhere && alreadyReceivingCareClinic.trim().length === 0) {
      errors.alreadyReceivingCareClinic = 'Choose the clinic they are seen at.';
    }

    if (teamMemberWellnessCenter.trim().length === 0) {
      errors.teamMemberWellnessCenter =
        'Choose the Chiro One Wellness Center. Everyone on this enrollment is seen there.';
    }

    const teamMemberPhoneError = validatePhone(teamMemberContactPhoneNumber, true);
    if (teamMemberPhoneError) {
      errors.teamMemberContactPhoneNumber = teamMemberPhoneError;
    }

    if (includeSpouse) {
      if (spouse.firstName.trim().length === 0) {
        errors['spouse.firstName'] = 'Enter a first name.';
      }
      if (spouse.lastName.trim().length === 0) {
        errors['spouse.lastName'] = 'Enter a last name.';
      }
      const phoneError = validatePhone(spouse.contactPhoneNumber, true);
      if (phoneError) {
        errors['spouse.contactPhoneNumber'] = phoneError;
      }
      // Not spouse.existingDocumentFileName: a switch between spouse and domestic partner retires it.
      const documentError = validateDocument(spouse.file, usableExistingDocument(spouse));
      if (documentError) {
        errors['spouse.document'] =
          documentError === 'Attach the required document.' && spouse.existingDocumentFileName
            ? `Attach ${spouse.type === 'spouse' ? 'a copy of your marriage certificate' : 'an Affidavit of Domestic Partnership'} for the relationship you switched to.`
            : documentError;
      }
    }

    children.forEach((child, index) => {
      if (child.firstName.trim().length === 0) {
        errors[`child.${index}.firstName`] = 'Enter a first name.';
      }
      if (child.lastName.trim().length === 0) {
        errors[`child.${index}.lastName`] = 'Enter a last name.';
      }
      const age = Number(child.age);
      if (child.age.trim().length === 0 || !Number.isInteger(age) || age < 0 || age >= MAX_ELIGIBLE_CHILD_AGE) {
        errors[`child.${index}.age`] = `Only children under ${MAX_ELIGIBLE_CHILD_AGE} are eligible.`;
      }
      // Optional for a child — they are reached through their parent.
      const phoneError = validatePhone(child.contactPhoneNumber, false);
      if (phoneError) {
        errors[`child.${index}.contactPhoneNumber`] = phoneError;
      }
      const documentError = validateDocument(child.file, child.existingDocumentFileName);
      if (documentError) {
        errors[`child.${index}.document`] = documentError;
      }
    });

    return errors;
  }

  /**
   * Opens a document that is already stored, which is only possible once the enrollee has been saved
   * at least once — before that there is an id but nothing behind it, and after a spouse/partner
   * switch the stored file is not the one this field is asking for.
   */
  function previewSaved(
    draft: { id?: string; firstName: string; lastName: string },
    fileName: string | null,
  ): (() => void) | undefined {
    const enrolleeId = draft.id;

    if (!enrolleeId || !fileName) {
      return undefined;
    }

    return () =>
      setPreviewing({
        title: `${`${draft.firstName} ${draft.lastName}`.trim()} — ${fileName}`,
        fileName,
        load: (signal) => api.downloadMyEnrolleeDocument(enrolleeId, signal),
      });
  }

  /** Builds the enrollees as the confirmation dialog reads them — the team member included, since a
   *  changed phone number of theirs is a change like any other. The wellness center sits beside them
   *  rather than on each row: one center covers everyone, so it reads as one line. */
  function draftForComparison(): Draft {
    const teamMember = editBase?.enrollees.find((e) => e.relationship === 'teamMember');

    return {
      hireDate,
      alreadyReceivingCareElsewhere,
      alreadyReceivingCareWho,
      alreadyReceivingCareClinic,
      wellnessCenter: teamMemberWellnessCenter,
      enrollees: [
        {
          id: teamMember?.id,
          relationship: 'teamMember',
          // Carried over rather than read off the form: the team member's name comes from the
          // directory, so the form has no field for it and nothing here can have changed it.
          firstName: teamMember?.firstName ?? '',
          lastName: teamMember?.lastName ?? '',
          age: teamMember?.age?.toString() ?? '',
          contactPhoneNumber: teamMemberContactPhoneNumber,
          fileName: null,
          existingDocumentFileName: teamMember?.documentFileName ?? null,
        },
        ...(includeSpouse
          ? [
              {
                id: spouse.id,
                relationship: spouse.type,
                firstName: spouse.firstName,
                lastName: spouse.lastName,
                age: spouse.age,
                contactPhoneNumber: spouse.contactPhoneNumber,
                fileName: spouse.file?.name ?? null,
                existingDocumentFileName: usableExistingDocument(spouse),
              } satisfies DraftEnrollee,
            ]
          : []),
        ...children.map(
          (child) =>
            ({
              id: child.id,
              relationship: 'child',
              firstName: child.firstName,
              lastName: child.lastName,
              age: child.age,
              contactPhoneNumber: child.contactPhoneNumber,
              fileName: child.file?.name ?? null,
              existingDocumentFileName: child.existingDocumentFileName,
            }) satisfies DraftEnrollee,
        ),
      ],
    };
  }

  /**
   * Validates, then asks. Nothing is sent from here — an enrollment going back to HR is worth one
   * deliberate confirmation, and the dialog is where the employee sees what they actually changed.
   */
  function handleSubmit(event: FormEvent) {
    event.preventDefault();

    const errors = validate();
    setFieldErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    setConfirming(
      editBase
        ? describeDraftChanges(editBase, draftForComparison())
        : { header: [], added: [], changed: [], removed: [] },
    );
  }

  async function send() {
    const dependents: EnrolleeSubmission[] = [];
    // Indexed to match `dependents`; a null means "keep the document already on file".
    const documents: (File | null)[] = [];

    if (includeSpouse) {
      dependents.push({
        id: spouse.id,
        relationship: spouse.type,
        firstName: spouse.firstName.trim(),
        lastName: spouse.lastName.trim(),
        age: spouse.age.trim() ? Number(spouse.age) : null,
        contactPhoneNumber: spouse.contactPhoneNumber.trim(),
      });
      documents.push(spouse.file);
    }

    for (const child of children) {
      dependents.push({
        id: child.id,
        relationship: 'child',
        firstName: child.firstName.trim(),
        lastName: child.lastName.trim(),
        age: Number(child.age),
        contactPhoneNumber: child.contactPhoneNumber.trim() || null,
      });
      documents.push(child.file);
    }

    const form = new FormData();
    form.set('hireDate', hireDate);
    form.set('alreadyReceivingCareElsewhere', alreadyReceivingCareElsewhere ? 'true' : 'false');
    form.set('alreadyReceivingCareWho', alreadyReceivingCareWho.trim());
    form.set('alreadyReceivingCareClinic', alreadyReceivingCareClinic.trim());
    form.set('teamMemberWellnessCenter', teamMemberWellnessCenter.trim());
    form.set('teamMemberContactPhoneNumber', teamMemberContactPhoneNumber.trim());
    form.set('enrollees', JSON.stringify(dependents));
    documents.forEach((file, index) => {
      if (file) {
        form.set(`document_${index}`, file);
      }
    });

    setSubmitting(true);
    setSubmitError(null);

    try {
      await api.submitComplimentaryCareEnrollment(form);

      // Re-read rather than patch: a submission can land in either row, and the server is the only
      // thing that knows which.
      await reload();
      setEditing(false);
      setConfirming(null);
      setRemovedChildren([]);
      setFieldErrors({});
    } catch (err) {
      setConfirming(null);

      if (err instanceof ApiError) {
        setSubmitError(err.message);

        // Put whatever the API rejected back on the field it belongs to. Most of its rules are
        // mirrored here and never get this far, but not all of them are — a clinic name the
        // directory does not have is the API's to judge — and a banner alone leaves the employee
        // hunting for which field it meant. Keys it uses for fields this form names differently are
        // simply never read.
        setFieldErrors(
          Object.fromEntries(
            Object.entries(err.fieldErrors).map(([field, messages]) => [field, messages.join(' ')]),
          ),
        );
      } else {
        setSubmitError(err instanceof Error ? err.message : 'The submission failed.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (loadError) {
    return (
      <div className="page-message page-message--error">
        <h2>Could not load the enrollment form</h2>
        <p>{loadError}</p>
      </div>
    );
  }

  if (!loaded) {
    return <div className="page-message">Loading…</div>;
  }

  const showForm = editing || enrollment === null;

  const openPreview = (enrollee: Enrollee) =>
    setPreviewing({
      title: `${enrollee.name} — ${enrollee.documentFileName}`,
      fileName: enrollee.documentFileName ?? 'document',
      load: (signal) => api.downloadMyEnrolleeDocument(enrollee.id, signal),
    });

  return (
    <div className="care-page">
      <header className="care-page__header">
        <h1 className="care-page__title">Employee Complimentary Chiropractic Care</h1>
        <p className="care-page__subtitle">
          Enrollment for you and your eligible family. HR reviews every submission before the clinic
          calls to schedule the first appointments.
        </p>
      </header>

      <div className="care-eligibility">
        <section className="care-eligibility__panel care-eligibility__panel--yes">
          <h2 className="care-eligibility__title">Who is eligible</h2>
          <ul>
            <li>You, as a Full-Time team member</li>
            <li>Your spouse (marriage certificate) or domestic partner (affidavit)</li>
            <li>
              Your children under {MAX_ELIGIBLE_CHILD_AGE} in the same household (birth certificate or
              adoption agreement)
            </li>
          </ul>
        </section>

        <section className="care-eligibility__panel care-eligibility__panel--no">
          <h2 className="care-eligibility__title">Who is not eligible</h2>
          <ul>
            <li>Parents, grandparents, siblings and cousins</li>
            <li>Extended family and friends</li>
          </ul>
          <p className="care-eligibility__note">
            They can still be seen through the regular patient process.
          </p>
        </section>
      </div>

      {!showForm && enrollment && (
        <>
          <EnrollmentStanding enrollment={enrollment} proposedEdit={proposedEdit} />

          <EnrollmentSummary
            enrollment={enrollment}
            heading={enrollment.isActive ? 'Your active enrollment' : 'Your enrollment'}
            onPreview={openPreview}
          />

          {/* The change with HR, shown separately so it is never mistaken for what is in force. */}
          {proposedEdit && (
            <EnrollmentSummary
              enrollment={proposedEdit}
              heading={
                proposedEdit.kind === 'reEnrollment' ? 'Request waiting for HR' : 'Change waiting for HR'
              }
              onPreview={openPreview}
            />
          )}

          <div className="care-form__actions">
            <button type="button" className="button button--primary" onClick={() => startEditing(editBase)}>
              {/*
                A deactivated enrollment is not edited — HR switched it off, and letting the employee
                reopen it would put them back in the review queue as though that decision had never
                been made. The same button submits a fresh request instead, which lands beside the
                deactivated record rather than over it.
              */}
              {proposedEdit
                ? proposedEdit.kind === 'reEnrollment'
                  ? 'Edit the request'
                  : 'Edit the change'
                : enrollment.status === 'deactivated'
                  ? 'Request enrollment again'
                  : 'Edit enrollment'}
            </button>
          </div>
        </>
      )}

      {showForm && (
        <form className="care-form" onSubmit={handleSubmit} noValidate>
          {editBase && (
            <div className="issues__block issues__block--warning">
              <p className="issues__title">
                {proposedEdit
                  ? proposedEdit.kind === 'reEnrollment'
                    ? 'Editing the request HR has not reviewed yet'
                    : 'Editing the change HR has not reviewed yet'
                  : enrollment?.status === 'deactivated'
                    ? 'Asking to be enrolled again'
                    : 'Editing your enrollment'}
              </p>
              <p>
                {enrollment?.isActive
                  ? 'Saving sends the change to HR. Your enrollment stays active until they approve it.'
                  : enrollment?.status === 'deactivated'
                    ? 'Your previous enrollment was switched off and stays that way — this goes to HR as a new request, filled in with what you had before. Correct anything that has changed.'
                    : 'Saving sends it back to HR for review, along with a summary of what you changed.'}
              </p>
            </div>
          )}

          <section className="group">
            <h2 className="group__legend">Enrollment form</h2>

            <div className="care-fields">
              <div className="field">
                <label className="field__label" htmlFor="hireDate">
                  Your hire date <span className="field__required">*</span>
                </label>
                <input
                  id="hireDate"
                  type="date"
                  className={fieldErrors.hireDate ? 'control control--invalid' : 'control'}
                  value={hireDate}
                  max={todayIso()}
                  onChange={(e) => setHireDate(e.target.value)}
                />
                {fieldErrors.hireDate && <p className="field__error">{fieldErrors.hireDate}</p>}
              </div>
            </div>

            <label className="checkbox care-checkbox">
              <input
                type="checkbox"
                checked={alreadyReceivingCareElsewhere}
                onChange={(e) => setAlreadyReceivingCareElsewhere(e.target.checked)}
              />
              <span>Someone listed below is already receiving care at a Chiro One Wellness Center</span>
            </label>

            {/* Two answers rather than one sentence: the clinic half is a name out of the directory,
                and asking for it on its own is what lets it be chosen instead of typed. */}
            {alreadyReceivingCareElsewhere && (
              <div className="care-fields care-checkbox__fields">
                <div className="field">
                  <label className="field__label" htmlFor="alreadyReceivingCareWho">
                    Who <span className="field__required">*</span>
                  </label>
                  <input
                    id="alreadyReceivingCareWho"
                    className={fieldErrors.alreadyReceivingCareWho ? 'control control--invalid' : 'control'}
                    placeholder="Who is already being seen"
                    value={alreadyReceivingCareWho}
                    onChange={(e) => setAlreadyReceivingCareWho(e.target.value)}
                    maxLength={300}
                  />
                  {fieldErrors.alreadyReceivingCareWho && (
                    <p className="field__error">{fieldErrors.alreadyReceivingCareWho}</p>
                  )}
                </div>

                <ClinicField
                  id="alreadyReceivingCareClinic"
                  label="At which clinic"
                  clinics={clinics}
                  value={alreadyReceivingCareClinic}
                  errors={fieldErrors.alreadyReceivingCareClinic ? [fieldErrors.alreadyReceivingCareClinic] : undefined}
                  onChange={setAlreadyReceivingCareClinic}
                />
              </div>
            )}
          </section>

          <section className="group">
            <h2 className="group__legend">New enrollees</h2>

            <div className="care-block">
              <div className="care-block__header">
                <h3 className="care-block__title">Team Member (You)</h3>
                <span className="care-block__meta">{userLabel}</span>
              </div>

              <div className="care-fields">
                <ClinicField
                  id="teamMemberWellnessCenter"
                  label="Chiro One Wellness Center"
                  clinics={clinics}
                  value={teamMemberWellnessCenter}
                  errors={fieldErrors.teamMemberWellnessCenter ? [fieldErrors.teamMemberWellnessCenter] : undefined}
                  hint="Everyone on this enrollment is seen here — your spouse or domestic partner and your children go to the same Wellness Center as you."
                  onChange={setTeamMemberWellnessCenter}
                />

                <PhoneField
                  id="teamMemberContactPhoneNumber"
                  required
                  value={teamMemberContactPhoneNumber}
                  error={fieldErrors.teamMemberContactPhoneNumber}
                  onChange={setTeamMemberContactPhoneNumber}
                />
              </div>
            </div>

            <div className="care-block">
              <div className="care-block__header">
                <h3 className="care-block__title">
                  Spouse or Domestic Partner
                  {/* The paper form's "Circle One": one row, one person, either kind. */}
                  <span className="care-block__meta">One only</span>
                </h3>
                <label className="checkbox">
                  <input
                    type="checkbox"
                    checked={includeSpouse}
                    onChange={(e) => setIncludeSpouse(e.target.checked)}
                  />
                  <span>Enroll one</span>
                </label>
              </div>

              {/* Unticking is the way to take someone off, so it says what saving would do rather than
                  silently dropping a person and their certificate. */}
              {!includeSpouse && spouse.id && (
                <div className="issues__block issues__block--warning">
                  <p className="issues__title">
                    {`${spouse.firstName} ${spouse.lastName}`.trim()} will be removed
                  </p>
                  <p>
                    Saving takes them off the enrollment and deletes the document they were enrolled
                    with. Tick the box again to keep them.
                  </p>
                </div>
              )}

              {includeSpouse && (
                <SpouseFields
                  spouse={spouse}
                  onChange={setSpouse}
                  errors={fieldErrors}
                  onPreviewDocument={previewSaved(spouse, usableExistingDocument(spouse))}
                />
              )}
            </div>

            <div className="care-block">
              <div className="care-block__header">
                <h3 className="care-block__title">
                  Children
                  {children.length > 0 && (
                    <span className="care-block__meta">
                      {children.length} of {MAX_CHILDREN}
                    </span>
                  )}
                </h3>
                {children.length < MAX_CHILDREN && (
                  <button
                    type="button"
                    className="button button--quiet button--small"
                    onClick={() => setChildren((prev) => [...prev, emptyChild()])}
                  >
                    Add a child
                  </button>
                )}
              </div>

              <p className="care-block__note">List the children living in your household.</p>

              {children.map((child, index) => (
                <ChildFields
                  key={child.key}
                  index={index}
                  child={child}
                  errors={fieldErrors}
                  onPreviewDocument={previewSaved(child, child.existingDocumentFileName)}
                  onChange={(updated) => setChildren((prev) => prev.map((c, i) => (i === index ? updated : c)))}
                  onRemove={() => {
                    setRemovedChildren((prev) => [{ at: index, child }, ...prev]);
                    setChildren((prev) => prev.filter((_, i) => i !== index));
                  }}
                />
              ))}

              {/* Removing is one click, so undoing it is one click too — rather than a confirmation
                  dialog on every removal, which is a tax on the many to protect against the few. The
                  child is only really gone once the form is submitted. */}
              {removedChildren.map(({ at, child }) => (
                <div className="care-undo" key={child.key}>
                  <span>
                    Removed <strong>{`${child.firstName} ${child.lastName}`.trim() || `Child #${at + 1}`}</strong>
                    {child.existingDocumentFileName && ' — their document goes with them when you save'}
                  </span>
                  <button
                    type="button"
                    className="button button--quiet button--small"
                    onClick={() => {
                      setChildren((prev) => {
                        const next = [...prev];
                        next.splice(Math.min(at, next.length), 0, child);
                        return next;
                      });
                      setRemovedChildren((prev) => prev.filter((r) => r.child.key !== child.key));
                    }}
                  >
                    Undo
                  </button>
                </div>
              ))}
            </div>
          </section>

          {submitError && (
            <div className="issues__block issues__block--error">
              <p className="issues__title">Could not submit the form</p>
              <p>{submitError}</p>
            </div>
          )}

          <div className="care-form__actions">
            {editBase && (
              <button type="button" className="button button--quiet" onClick={() => setEditing(false)}>
                Cancel
              </button>
            )}
            <button type="submit" className="button button--primary" disabled={submitting}>
              {editBase ? 'Review and send' : 'Review and submit'}
            </button>
          </div>
        </form>
      )}

      {previewing && <DocumentPreviewModal target={previewing} onClose={() => setPreviewing(null)} />}

      <SubmitConfirmModal
        changes={confirming}
        firstSubmission={editBase === null}
        busy={submitting}
        onConfirm={send}
        onCancel={() => setConfirming(null)}
      />
    </div>
  );
}

/**
 * How the employee's enrollment stands right now, in one sentence at the top of the page.
 *
 * Worth its own component because the interesting cases are the ones where the newest version and the
 * live one are different rows: a rejected edit must not read as "you have no care", which is what a
 * single status badge on the newest version would have said.
 */
function EnrollmentStanding({
  enrollment,
  proposedEdit,
}: {
  enrollment: ComplimentaryCareEnrollment;
  proposedEdit: ComplimentaryCareEnrollment | null;
}) {
  return (
    <>
      {proposedEdit?.status === 'rejected' && enrollment.isActive && (
        <div className="issues__block issues__block--warning">
          <p className="issues__title">Your change was not approved — your care is still active</p>
          <p>
            HR turned the change down
            {proposedEdit.reviewNote ? `: “${proposedEdit.reviewNote}”` : '.'} The enrollment below,
            approved{' '}
            {enrollment.reviewedAtUtc ? new Date(enrollment.reviewedAtUtc).toLocaleDateString() : 'earlier'},
            is still the one you are enrolled on. Edit and send again when you have what HR asked for.
          </p>
        </div>
      )}

      {proposedEdit?.status === 'pendingReview' && enrollment.isActive && (
        <div className="issues__block issues__block--warning">
          <p className="issues__title">A change is with HR</p>
          <p>
            Until it is approved you stay enrolled on what is below — nothing you changed is in force
            yet.
          </p>
        </div>
      )}

      {enrollment.status === 'rejected' && (
        <div className="issues__block issues__block--error">
          <p className="issues__title">HR could not approve this yet</p>
          <p>{enrollment.reviewNote ?? 'Contact HR for details.'}</p>
        </div>
      )}

      {/* A re-enrollment leaves the deactivated record standing, so "no longer active" is still true
          and still the headline — but on its own it reads as though the request went nowhere. */}
      {enrollment.status === 'deactivated' && (
        <div className="issues__block issues__block--error">
          <p className="issues__title">No longer active</p>
          <p>
            {proposedEdit?.status === 'pendingReview'
              ? 'Your request to be enrolled again is with HR. Until they approve it, you are not enrolled.'
              : proposedEdit?.status === 'rejected'
                ? `HR did not approve your request${proposedEdit.reviewNote ? `: “${proposedEdit.reviewNote}”` : '.'} You can correct it and ask again.`
                : 'Contact HR if this is wrong.'}
          </p>
        </div>
      )}
    </>
  );
}

function EnrollmentSummary({
  enrollment,
  heading,
  onPreview,
}: {
  enrollment: ComplimentaryCareEnrollment;
  heading: string;
  onPreview: (enrollee: Enrollee) => void;
}) {
  const submitted = new Date(enrollment.submittedAtUtc).toLocaleString();

  return (
    <section className="group care-summary">
      <div className="care-block__header">
        <h2 className="group__legend group__legend--flush">{heading}</h2>
        <EnrollmentStatusBadge status={enrollment.status} />
      </div>

      <p className="care-form__hint">
        {enrollment.resubmittedAtUtc
          ? `Submitted ${submitted}, last edited ${new Date(enrollment.resubmittedAtUtc).toLocaleString()}`
          : `Submitted ${submitted}`}
        {enrollment.status === 'pendingReview' && ' — waiting for HR to validate the documents.'}
        {enrollment.status === 'approved' && ' — approved, the clinic will call to schedule the first appointments.'}
        {enrollment.status === 'deactivated' && ' — no longer active. Contact HR if this is wrong.'}
      </p>

      {/*
        The answers above the table are part of the enrollment too. Without them a change to only the
        hire date or the "already receiving care" question produces two cards that look identical, and
        the employee is left comparing a change they cannot see against the one in force.
      */}
      <dl className="care-summary__facts">
        <div className="care-summary__fact">
          <dt>Hire date</dt>
          <dd>{formatIsoDate(enrollment.hireDate)}</dd>
        </div>
        <div className="care-summary__fact">
          <dt>Chiro One Wellness Center</dt>
          <dd>{enrollment.enrollees.find((e) => e.relationship === 'teamMember')?.wellnessCenter ?? '—'}</dd>
        </div>
        <div className="care-summary__fact">
          <dt>Already receiving care at Chiro One</dt>
          <dd>{describeExistingCare(enrollment)}</dd>
        </div>
      </dl>

      <table className="care-table care-table--summary">
        <thead>
          <tr>
            <th>Relationship</th>
            <th>Name</th>
            <th>Age</th>
            <th>Contact phone</th>
            <th>Document</th>
          </tr>
        </thead>
        <tbody>
          {enrollment.enrollees.map((enrollee) => (
            <tr key={enrollee.id}>
              <td>{relationshipLabel(enrollee.relationship)}</td>
              <td>{enrollee.name}</td>
              <td>{enrollee.age ?? '—'}</td>
              <td>{enrollee.contactPhoneNumber ?? '—'}</td>
              <td>
                {enrollee.documentFileName ? (
                  <button type="button" className="care-document-link" onClick={() => onPreview(enrollee)}>
                    {enrollee.documentFileName}
                  </button>
                ) : (
                  '—'
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

interface PhoneFieldProps {
  id?: string;
  value: string;
  error: string | undefined;
  required: boolean;
  onChange: (value: string) => void;
}

function PhoneField({ id, value, error, required, onChange }: PhoneFieldProps) {
  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        Contact phone number{required && <span className="field__required"> *</span>}
      </label>
      <input
        id={id}
        type="tel"
        inputMode="numeric"
        autoComplete="tel-national"
        // The brackets are the instruction: the caret starts inside them, so the area code is what
        // the field asks for first.
        placeholder="(___) ___-____"
        className={error ? 'control control--invalid' : 'control'}
        value={value}
        onChange={(e) => onChange(formatPhoneInput(e.target.value, value))}
        maxLength={14}
      />
      {error && <p className="field__error">{error}</p>}
    </div>
  );
}

interface DocumentFieldProps {
  label: string;
  error: string | undefined;
  file: File | null;
  existingDocumentFileName: string | null;

  /** Absent until the enrollee has been saved once — there is nothing stored to open before that. */
  onPreview?: () => void;

  onChange: (file: File | null) => void;
}

function DocumentField({
  label,
  error,
  file,
  existingDocumentFileName,
  onPreview,
  onChange,
}: DocumentFieldProps) {
  // Exactly three children — label, control, message — so the subgrid keeps every field in a row
  // lined up. A fourth would spill into the next field's label.
  return (
    <div className="field care-fields__wide">
      <label className="field__label">
        {label} <span className="field__required">*</span>
      </label>
      <input
        type="file"
        className={error ? 'control control--invalid' : 'control'}
        accept={ALLOWED_EXTENSIONS.join(',')}
        onChange={(e) => onChange(e.target.files?.[0] ?? null)}
      />
      {error ? (
        <p className="field__error">{error}</p>
      ) : file ? (
        <p className="care-file care-file--new">
          <span aria-hidden="true">📎</span> New file: <strong>{file.name}</strong>
        </p>
      ) : existingDocumentFileName ? (
        <p className="care-file">
          <span aria-hidden="true">📎</span> Attached:{' '}
          {/* Openable, because "is the right certificate on file?" is a question about the document
              itself, which its filename only half answers. */}
          {onPreview ? (
            <button type="button" className="care-document-link" onClick={onPreview}>
              {existingDocumentFileName}
            </button>
          ) : (
            <strong>{existingDocumentFileName}</strong>
          )}
          <span className="care-file__hint">Choose a file only to replace it.</span>
        </p>
      ) : (
        <p className="field__help">PDF, JPG or PNG, up to 10 MB.</p>
      )}
    </div>
  );
}

interface SpouseFieldsProps {
  spouse: SpouseDraft;
  errors: Record<string, string>;
  onPreviewDocument?: () => void;
  onChange: (spouse: SpouseDraft) => void;
}

function SpouseFields({ spouse, errors, onPreviewDocument, onChange }: SpouseFieldsProps) {
  const isSpouse = spouse.type === 'spouse';

  // The form is back on the relationship it was saved under, or was never saved: nothing has been
  // retired and the document field behaves normally.
  const documentRetired =
    spouse.documentRelationship !== null && spouse.documentRelationship !== spouse.type;

  return (
    <div className="care-fields">
      <div className="field care-fields__wide">
        <span className="field__label">
          Circle one <span className="field__required">*</span>
        </span>
        <div className="care-radio-group">
          <label className="checkbox">
            <input
              type="radio"
              name="spouseType"
              checked={isSpouse}
              onChange={() => onChange({ ...spouse, type: 'spouse' })}
            />
            <span>Spouse</span>
          </label>
          <label className="checkbox">
            <input
              type="radio"
              name="spouseType"
              checked={!isSpouse}
              onChange={() => onChange({ ...spouse, type: 'domesticPartner' })}
            />
            <span>Domestic Partner</span>
          </label>
        </div>
      </div>

      {documentRetired && (
        <div className="care-fields__wide issues__block issues__block--warning">
          <p className="issues__title">
            {isSpouse ? 'Switched to Spouse' : 'Switched to Domestic Partner'}
          </p>
          <p>
            The {spouse.documentRelationship === 'spouse' ? 'marriage certificate' : 'affidavit'} on
            file proves the other relationship, so it no longer applies here. Attach{' '}
            {isSpouse ? 'a copy of your marriage certificate' : 'an Affidavit of Domestic Partnership'}{' '}
            below to save this change — everything else can stay as it is.
          </p>
        </div>
      )}

      <div className="field">
        <label className="field__label">
          First name <span className="field__required">*</span>
        </label>
        <input
          className={errors['spouse.firstName'] ? 'control control--invalid' : 'control'}
          value={spouse.firstName}
          onChange={(e) => onChange({ ...spouse, firstName: e.target.value })}
          maxLength={100}
        />
        {errors['spouse.firstName'] && <p className="field__error">{errors['spouse.firstName']}</p>}
      </div>

      <div className="field">
        <label className="field__label">
          Last name <span className="field__required">*</span>
        </label>
        <input
          className={errors['spouse.lastName'] ? 'control control--invalid' : 'control'}
          value={spouse.lastName}
          onChange={(e) => onChange({ ...spouse, lastName: e.target.value })}
          maxLength={100}
        />
        {errors['spouse.lastName'] && <p className="field__error">{errors['spouse.lastName']}</p>}
      </div>

      <div className="field care-fields__narrow">
        <label className="field__label">Age</label>
        <input
          type="number"
          min={0}
          className="control"
          value={spouse.age}
          onChange={(e) => onChange({ ...spouse, age: e.target.value })}
        />
      </div>

      <PhoneField
        required
        value={spouse.contactPhoneNumber}
        error={errors['spouse.contactPhoneNumber']}
        onChange={(value) => onChange({ ...spouse, contactPhoneNumber: value })}
      />

      <DocumentField
        label={isSpouse ? 'Copy of your marriage certificate' : 'Affidavit of Domestic Partnership'}
        error={errors['spouse.document']}
        file={spouse.file}
        existingDocumentFileName={usableExistingDocument(spouse)}
        onPreview={onPreviewDocument}
        onChange={(file) => onChange({ ...spouse, file })}
      />
    </div>
  );
}

interface ChildFieldsProps {
  index: number;
  child: ChildDraft;
  errors: Record<string, string>;
  onPreviewDocument?: () => void;
  onChange: (child: ChildDraft) => void;
  onRemove: () => void;
}

function ChildFields({ index, child, errors, onPreviewDocument, onChange, onRemove }: ChildFieldsProps) {
  return (
    <div className="care-child">
      <div className="care-child__header">
        <h4 className="care-child__title">Child #{index + 1}</h4>
        <button type="button" className="button button--quiet button--small" onClick={onRemove}>
          Remove
        </button>
      </div>

      <div className="care-fields">
        <div className="field">
          <label className="field__label">
            First name <span className="field__required">*</span>
          </label>
          <input
            className={errors[`child.${index}.firstName`] ? 'control control--invalid' : 'control'}
            value={child.firstName}
            onChange={(e) => onChange({ ...child, firstName: e.target.value })}
            maxLength={100}
          />
          {errors[`child.${index}.firstName`] && (
            <p className="field__error">{errors[`child.${index}.firstName`]}</p>
          )}
        </div>

        <div className="field">
          <label className="field__label">
            Last name <span className="field__required">*</span>
          </label>
          <input
            className={errors[`child.${index}.lastName`] ? 'control control--invalid' : 'control'}
            value={child.lastName}
            onChange={(e) => onChange({ ...child, lastName: e.target.value })}
            maxLength={100}
          />
          {errors[`child.${index}.lastName`] && (
            <p className="field__error">{errors[`child.${index}.lastName`]}</p>
          )}
        </div>

        <div className="field care-fields__narrow">
          <label className="field__label">
            Age <span className="field__required">*</span>
          </label>
          <input
            type="number"
            min={0}
            max={MAX_ELIGIBLE_CHILD_AGE - 1}
            className={errors[`child.${index}.age`] ? 'control control--invalid' : 'control'}
            value={child.age}
            onChange={(e) => onChange({ ...child, age: e.target.value })}
          />
          {errors[`child.${index}.age`] && <p className="field__error">{errors[`child.${index}.age`]}</p>}
        </div>

        <PhoneField
          required={false}
          value={child.contactPhoneNumber}
          error={errors[`child.${index}.contactPhoneNumber`]}
          onChange={(value) => onChange({ ...child, contactPhoneNumber: value })}
        />

        <DocumentField
          label="Copy of birth certificate or adoption agreement"
          error={errors[`child.${index}.document`]}
          file={child.file}
          existingDocumentFileName={child.existingDocumentFileName}
          onPreview={onPreviewDocument}
          onChange={(file) => onChange({ ...child, file })}
        />
      </div>
    </div>
  );
}
